import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapVertIcon from "@mui/icons-material/SwapVert";

const SignerHistoryModal = ({ open, onClose, firmante, token }) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && firmante) {
      fetchHistory();
    }
  }, [open, firmante]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signers/${firmante.id}/history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error al obtener el historial del firmante");
      }

      const data = await response.json();
      setHistory(data.data || []);
    } catch (error) {
      console.error("Error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };
    return new Date(dateString).toLocaleString("es-CL", options);
  };

  // Función para renderizar los detalles según la acción
  const renderDetails = (action, details) => {
    if (!details) return <span>Sin detalles</span>;

    switch (action) {
      case "create":
        return (
          <Box>
            {details.is_manual ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Firmante manual creado
                </Typography>
                <Typography variant="body2">
                  Nombre: {details.first_name} {details.last_name}
                </Typography>
                <Typography variant="body2">RUT: {details.rut}</Typography>
                <Typography variant="body2">Email: {details.email}</Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Firmante de usuario creado
                </Typography>
                <Typography variant="body2">
                  ID de usuario: {details.user_id}
                </Typography>
              </>
            )}
            <Typography variant="body2">
              Orden de firma: {details.signing_order}
            </Typography>
            <Typography variant="body2">
              Activo: {details.is_active ? "Sí" : "No"}
            </Typography>
          </Box>
        );

      case "update":
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Campos actualizados:
            </Typography>
            {details.manual_first_name !== undefined && (
              <Typography variant="body2">
                Nombre: {details.manual_first_name}
              </Typography>
            )}
            {details.manual_last_name !== undefined && (
              <Typography variant="body2">
                Apellido: {details.manual_last_name}
              </Typography>
            )}
            {details.manual_rut !== undefined && (
              <Typography variant="body2">RUT: {details.manual_rut}</Typography>
            )}
            {details.manual_email !== undefined && (
              <Typography variant="body2">
                Email: {details.manual_email}
              </Typography>
            )}
            {details.signing_order !== undefined && (
              <Typography variant="body2">
                Orden de firma: {details.signing_order}
              </Typography>
            )}
            {details.is_active !== undefined && (
              <Typography variant="body2">
                Activo: {details.is_active ? "Sí" : "No"}
              </Typography>
            )}
          </Box>
        );

      case "delete":
        return <Typography variant="body2">Firmante eliminado</Typography>;

      case "reorder":
        return (
          <Typography variant="body2">
            Nuevo orden de firma: {details.new_signing_order}
          </Typography>
        );

      default:
        return <span>Sin detalles</span>;
    }
  };

  // Icono para cada tipo de acción
  const getActionIcon = (action) => {
    switch (action) {
      case "create":
        return <AddIcon fontSize="small" />;
      case "update":
        return <EditIcon fontSize="small" />;
      case "delete":
        return <DeleteIcon fontSize="small" />;
      case "reorder":
        return <SwapVertIcon fontSize="small" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  // Color para cada tipo de acción
  const getActionColor = (action) => {
    switch (action) {
      case "create":
        return "success";
      case "update":
        return "primary";
      case "delete":
        return "error";
      case "reorder":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="historial-firmante-dialog"
    >
      <DialogTitle id="historial-firmante-dialog">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Historial de cambios del firmante
            {firmante && (
              <Typography component="span" variant="subtitle1" sx={{ ml: 1 }}>
                {firmante.first_name} {firmante.last_name}
              </Typography>
            )}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
          </Typography>
        ) : history.length === 0 ? (
          <Typography align="center">
            No hay historial de cambios para este firmante
          </Typography>
        ) : (
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Acción</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Detalles</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getActionIcon(item.action)}
                        label={
                          item.action === "create"
                            ? "Creación"
                            : item.action === "update"
                            ? "Actualización"
                            : item.action === "delete"
                            ? "Eliminación"
                            : item.action === "reorder"
                            ? "Reordenamiento"
                            : item.action
                        }
                        size="small"
                        color={getActionColor(item.action)}
                      />
                    </TableCell>
                    <TableCell>{item.user_name || "Sistema"}</TableCell>
                    <TableCell>
                      {renderDetails(item.action, item.details)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SignerHistoryModal;
